import React, { useEffect, useState } from 'react';
import {
  ArrowUp,
  ArrowRight,
  Code2,
  Download,
  FileSearch,
  GitCompare,
  Github,
  History,
  Search,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';
import useDocumentTitle from '../hooks/useDocumentTitle';
import useOpenGraph from '../hooks/useOpenGraph';
import './HomePage.css';

const GITHUB_URL = 'https://github.com/WP-Autoplugin/wp-autoplugin';
const RELEASE_URL = `${GITHUB_URL}/releases/latest`;

const MediaPlaceholder = ({ label, src, alt, eager = false, className = '', children, ...props }) => (
  <div className={`media-placeholder${src ? ' has-product-media' : ''} ${className}`} {...props}>
    {src ? (
      <img
        className="product-media__image"
        src={src}
        alt={alt}
        loading={eager ? 'eager' : 'lazy'}
        fetchPriority={eager ? 'high' : 'auto'}
      />
    ) : (
      <>
        <div className="media-placeholder__label">
          <span>{label}</span>
          <span>Replace with product media</span>
        </div>
        {children}
      </>
    )}
  </div>
);

const WorkspacePreview = () => (
  <MediaPlaceholder
    label="Main workspace screenshot"
    src="/screenshots/workspace-overview.png"
    alt="WP-Autoplugin workspace showing durable project tabs, model and token controls, revision history, and stage-specific chat"
    className="workspace-preview product-media--hero"
    eager
  />
);

const useCases = [
  {
    number: '01',
    title: 'Create a plugin',
    description: 'Turn a requirement into a planned, reviewed, installable WordPress plugin.',
    previewLabel: 'Create plugin workspace screenshot',
    previewType: 'create',
  },
  {
    number: '02',
    title: 'Modify or fix existing code',
    description: 'Let the agent inspect a plugin or theme, understand its structure, and stage exact changes.',
    previewLabel: 'Existing-code workspace screenshot',
    previewType: 'modify',
  },
  {
    number: '03',
    title: 'Build an extension',
    description: 'Explore available hooks and create a separate add-on without changing the original target.',
    previewLabel: 'Extension workspace screenshot',
    previewType: 'extend',
  },
  {
    number: '04',
    title: 'Understand a codebase',
    description: 'Ask questions, trace behavior, search source, and keep the explanation with the project.',
    previewLabel: 'Codebase explanation screenshot',
    previewType: 'explain',
  },
];

const UseCasePreview = ({ item, activeIndex }) => {
  const previewContent = {
    create: (
      <div className="launcher-placeholder__content use-case-preview__content">
        <small>SELECT A TARGET</small>
        <div className="launcher-placeholder__search"><Search size={15} /> New WordPress plugin</div>
        <div className="launcher-placeholder__targets"><span className="is-selected">New plugin</span><span>Installed plugins</span><span>Themes</span></div>
        <small>WHAT DO YOU WANT TO BUILD?</small>
        <div className="use-case-preview__prompt">Add a configurable VAT number field to WooCommerce checkout.</div>
        <div className="use-case-preview__status"><span>Planner ready</span><b>Start Plan <ArrowRight size={13} /></b></div>
      </div>
    ),
    modify: (
      <div className="use-case-preview__content">
        <small>SELECTED TARGET</small>
        <div className="use-case-preview__target">
          <div><b>WooCommerce Checkout Fields</b><span>Installed plugin · 42 source files</span></div>
          <strong>Inspecting</strong>
        </div>
        <small>STAGED CHANGES</small>
        <div className="use-case-preview__files">
          <span><code>M</code> includes/class-validation.php</span>
          <span><code>M</code> includes/class-emails.php</span>
          <span><code>A</code> assets/admin-vat.js</span>
        </div>
        <div className="use-case-preview__status"><span>Revision #6</span><b>3 files changed</b></div>
      </div>
    ),
    extend: (
      <div className="use-case-preview__content">
        <small>DISCOVERED INTEGRATION POINTS</small>
        <div className="launcher-placeholder__search"><Search size={15} /> Search hooks in WooCommerce</div>
        <div className="use-case-preview__hooks">
          <span><code>action</code> woocommerce_checkout_process</span>
          <span><code>filter</code> woocommerce_checkout_fields</span>
          <span><code>action</code> woocommerce_email_order_meta</span>
        </div>
        <div className="use-case-preview__extension"><b>Separate extension plugin</b><span>Original target remains unchanged</span></div>
      </div>
    ),
    explain: (
      <div className="use-case-preview__content">
        <small>CODEBASE CONVERSATION</small>
        <div className="use-case-preview__question">How is checkout validation registered, and where are errors displayed?</div>
        <div className="use-case-preview__answer">
          <span className="wire-line is-wide" />
          <span className="wire-line" />
          <span className="wire-line is-short" />
        </div>
        <small>SOURCES EXPLORED</small>
        <div className="use-case-preview__sources"><span>includes/class-validation.php</span><span>includes/class-checkout.php</span></div>
        <div className="use-case-preview__status"><span>6 files inspected</span><b>Answer complete</b></div>
      </div>
    ),
  };

  return (
    <MediaPlaceholder
      id="use-case-preview"
      label={item.previewLabel}
      className="launcher-placeholder use-case-preview"
      role="region"
      aria-label={`${item.title} preview`}
      aria-live="polite"
      aria-atomic="true"
    >
      <div className={`use-case-preview__frame is-${item.previewType}`} key={item.number}>
        {previewContent[item.previewType]}
      </div>
      <div className="use-case-preview__rotation" aria-hidden="true">
        <div>{useCases.map((useCase, index) => <span className={index === activeIndex ? 'is-active' : ''} key={useCase.number} />)}</div>
        <small>{activeIndex + 1} / {useCases.length} · auto 8s</small>
      </div>
    </MediaPlaceholder>
  );
};

const workflowStages = [
  {
    number: '01',
    name: 'Plan',
    meta: 'Explore before editing',
    description: 'The agent inspects the target, follows project instructions, and turns the request into a versioned implementation plan.',
    detail: 'Plan v3 · 8 files · ready',
    media: '/screenshots/agent-activity.png',
    mediaAlt: 'WP-Autoplugin planning agent inspecting a WordPress plugin and presenting its implementation plan',
  },
  {
    number: '02',
    name: 'Code',
    meta: 'Stage a complete revision',
    description: 'Code is generated file by file, validated, and assembled into an immutable revision. Nothing is written to the target yet.',
    detail: 'revision #6 · 3 files changed',
    media: '/screenshots/targeted-diff.png',
    mediaAlt: 'WP-Autoplugin code stage showing a file tree and an exact targeted diff',
  },
  {
    number: '03',
    name: 'Review',
    meta: 'Review the exact revision',
    description: 'A separate reviewer checks the staged source against the plan and the latest accepted requirements, with findings tied to real files and lines.',
    detail: 'review complete · all clear',
    media: '/screenshots/review-findings.png',
    mediaAlt: 'WP-Autoplugin review stage showing a code finding, suggested fix, and reviewer follow-up controls',
  },
  {
    number: '04',
    name: 'Release',
    meta: 'You approve the write',
    description: 'Download the exact revision, install a safe copy, activate a new plugin, or explicitly approve a direct change when the target allows it.',
    detail: 'package ready · approval required',
    media: '/screenshots/release-workspace.png',
    mediaAlt: 'WP-Autoplugin release screen with safe deployment choices and manual testing instructions',
  },
];

const safetySteps = ['Installed plugin', 'Workspace', 'Revision', 'Review', 'Approval', 'Release'];

const HomePage = () => {
  const [activeUseCase, setActiveUseCase] = useState(0);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      return undefined;
    }

    const rotationTimer = window.setInterval(() => {
      setActiveUseCase((currentIndex) => (currentIndex + 1) % useCases.length);
    }, 8000);

    return () => window.clearInterval(rotationTimer);
  }, [activeUseCase]);

  useDocumentTitle(null);
  useOpenGraph({
    title: null,
    description: 'WP-Autoplugin is an agentic coding environment for WordPress. Explore code, plan changes, stage revisions, review, and release from one persistent workspace.',
  });
  
  return (
    <div className="v2-home">
      <section className="hero" id="home">
        <div className="site-container">
          <div className="hero__eyebrow">
            <span>WP-Autoplugin v2</span>
            <span>Open source</span>
            <span>Community-driven</span>
            <span>Bring-your-own-key</span>
          </div>
          <div className="hero__copy">
            <h1>Agentic coding,<br /><span>inside WordPress.</span></h1>
            <div className="hero__intro">
              <div className="hero__comparison">
                <Code2 size={16} />
                <span>Coding agent purpose-built for WordPress</span>
              </div>
              <p>
                Explore a codebase, plan changes, write and review code, and prepare
                releases from one persistent workspace in wp-admin.
              </p>
              <div className="hero__actions">
                <a className="button button--primary" href={RELEASE_URL} target="_blank" rel="noopener noreferrer">
                  <Download size={17} /> Download
                </a>
                <a className="button button--secondary" href={GITHUB_URL} target="_blank" rel="noopener noreferrer">
                  <Github size={17} /> GitHub
                </a>
              </div>
            </div>
          </div>
          <WorkspacePreview />
          <div className="hero__footnotes">
            <span><b>01</b> Durable workspaces</span>
            <span><b>02</b> Staged revisions</span>
            <span><b>03</b> Connect API key or ChatGPT</span>
            <span><b>04</b> Explicit release</span>
          </div>
        </div>
      </section>

      <section className="section section--workspace" id="workspace">
        <div className="site-container split-heading">
          <div>
            <p className="section-label">One workspace for every job</p>
            <h2>Build, fix, or extend<br /> WordPress code.</h2>
          </div>
          <p className="section-intro">
            Choose a target and describe what needs to happen. WP-Autoplugin brings
            the right planning, coding, review, or explanation flow into the same workspace.
          </p>
        </div>
        <div className="site-container workspace-use-cases">
          <div className="use-case-list">
            {useCases.map((item, index) => (
              <button
                type="button"
                className={`use-case ${index === activeUseCase ? 'is-active' : ''}`}
                key={item.number}
                onMouseEnter={() => setActiveUseCase(index)}
                onFocus={() => setActiveUseCase(index)}
                onClick={() => setActiveUseCase(index)}
                aria-pressed={index === activeUseCase}
                aria-controls="use-case-preview"
              >
                <span>{item.number}</span>
                <div>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </div>
                <ArrowRight size={22} />
              </button>
            ))}
          </div>
          <UseCasePreview item={useCases[activeUseCase]} activeIndex={activeUseCase} />
        </div>
      </section>

      <section className="section workflow" id="workflow">
        <div className="site-container workflow__header">
          <p className="section-label">The agentic workflow</p>
          <h2>Plan <ArrowRight /> Code <ArrowRight /> Review <ArrowRight /> Release</h2>
          <p>Each stage works from a durable artifact. You can inspect, discuss, revise, and continue without losing the thread.</p>
        </div>
        <div className="site-container workflow__stages">
          {workflowStages.map((stage) => (
            <article className="workflow-stage" key={stage.number}>
              <div className="workflow-stage__copy">
                <span className="workflow-stage__number">{stage.number}</span>
                <p className="workflow-stage__meta">{stage.meta}</p>
                <h3>{stage.name}</h3>
                <p>{stage.description}</p>
                <code>{stage.detail}</code>
              </div>
              <MediaPlaceholder
                label={`${stage.name} interface screenshot`}
                src={stage.media}
                alt={stage.mediaAlt}
                className={`workflow-stage__media workflow-stage__media--${stage.name.toLowerCase()}`}
              />
            </article>
          ))}
        </div>
      </section>

      <section className="demo-section" id="demo">
        <div className="site-container demo-section__header">
          <div>
            <p className="section-label section-label--light">See it in motion</p>
            <h2>Your ideas.<br />A new way to build.</h2>
          </div>
          <p>
            Follow a change to an existing checkout plugin from initial code exploration
            through a reviewed, release-ready revision.
            A quick look at what's possible with WP-Autoplugin. The agent inspects a target, plans changes, stages a revision, and prepares a safe release.
          </p>
        </div>
        <div className="site-container">
          <video
            className="promo-video"
            src="/wp-autoplugin-promo-public.mp4"
            poster="/wp-autoplugin-promo-poster.jpg"
            width="1920"
            height="1080"
            controls
            playsInline
            preload="none"
            aria-label="WP-Autoplugin promo video"
          >
            <a href="/wp-autoplugin-promo-public.mp4">Watch the WP-Autoplugin promo video.</a>
          </video>
        </div>
      </section>

      <section className="section safety" id="safety">
        <div className="site-container safety__heading">
          <p className="section-label">Safe by design</p>
          <h2>The agent never writes straight into live code.</h2>
          <p>
            Inspection is read-only. Generated changes live in an immutable staged revision.
            Releasing that revision is a separate, explicit administrator action.
          </p>
        </div>
        <div className="site-container safety-flow" aria-label="Safe release flow">
          {safetySteps.map((step, index) => (
            <React.Fragment key={step}>
              <div className={step === 'Approval' ? 'is-approval' : ''}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <b>{step}</b>
              </div>
              {index < safetySteps.length - 1 && <ArrowRight size={18} />}
            </React.Fragment>
          ))}
        </div>
        <div className="site-container safety__notes">
          <div><ShieldCheck size={22} /><span><b>Conflict-aware</b>Target changes are rechecked before staging or promotion.</span></div>
          <div><GitCompare size={22} /><span><b>Revision-first</b>Every change can be inspected as source and diff before release.</span></div>
          <div><History size={22} /><span><b>Recoverable</b>History, safe copies, packages, and guarded rollback remain available.</span></div>
        </div>
      </section>

      <section className="section persistent" id="persistent">
        <div className="site-container persistent__grid">
          <div className="persistent__copy">
            <p className="section-label">Persistent workspace</p>
            <h2>Keep the conversation with the code.</h2>
            <p>
              A workspace is a durable project, not a browser session. Close the tab,
              come back later, and the plan, revisions, review history, usage, and
              conversations are still there.
            </p>
            <ul>
              <li><History size={17} /> Immutable plan and revision history</li>
              <li><FileSearch size={17} /> Read-only source exploration and explanations</li>
              <li><GitCompare size={17} /> File tree, code editor, diffs, and manual successors</li>
              <li><Sparkles size={17} /> Follow-up conversations in every AI stage</li>
            </ul>
          </div>
          <MediaPlaceholder label="Persistent workspace screenshot" className="history-placeholder">
            <div className="history-placeholder__tabs"><span>Checkout fields</span><span>Theme fix</span><span>Plugin audit</span></div>
            <div className="history-placeholder__body">
              <div className="history-placeholder__timeline">
                <b>Revision history</b>
                <span className="is-current">#6 AI change · current</span>
                <span>#5 Manual edit</span>
                <span>#4 Review fix</span>
                <span>#3 Generated code</span>
              </div>
              <div className="history-placeholder__diff"><i /><i /><i className="is-add" /><i className="is-add" /><i /><i className="is-remove" /><i /></div>
            </div>
          </MediaPlaceholder>
        </div>
      </section>

      <section className="section models" id="models">
        <div className="site-container models__grid">
          <div>
            <p className="section-label">Use the models you want</p>
            <h2>Choose a model for each stage.</h2>
          </div>
          <div className="models__copy">
            <p>
              Set independent <strong>Planner</strong>, <strong>Coder</strong>, and <strong>Reviewer</strong> models. Use provider API keys,
              custom endpoints, or connect ChatGPT directly.
            </p>
            <div className="model-list">
              <span>OpenAI</span>
              <span>Anthropic</span>
              <span>Gemini</span>
              <span>xAI</span>
              <span>Custom endpoints</span>
              <span>ChatGPT / Codex</span>
            </div>
          </div>
          <MediaPlaceholder
            label="Model and provider settings screenshot"
            src="/screenshots/settings-models.png"
            alt="WP-Autoplugin settings for AI providers, ChatGPT connection, model choice, and custom instructions"
            className="models__media"
          />
        </div>
      </section>

      <section className="open-source">
        <div className="site-container open-source__grid">
          <p className="section-label section-label--light">Free, open source, BYOK</p>
          <h2>Your site.<br />Your code.<br />Your models.</h2>
          <div>
            <p>
              WP-Autoplugin is free and open source plugin. Bring your own provider credentials,
              connect a supported ChatGPT account, or use OpenRouter or any other OpenAI-compatible
              endpoint. Everything stays on your WordPress site except for
              the context sent to the model you choose.
            </p>
            <a href={GITHUB_URL} target="_blank" rel="noopener noreferrer">Read the source <ArrowRight size={16} /></a>
          </div>
        </div>
      </section>

      <section className="final-cta" id="download">
        <div className="site-container final-cta__inner">
          <div>
            <p className="section-label">WP-Autoplugin v2</p>
            <h2>Build WordPress software from inside WordPress.</h2>
          </div>
          <div className="final-cta__actions">
            <a className="button button--primary button--large" href={RELEASE_URL} target="_blank" rel="noopener noreferrer">
              <Download size={19} /> Download
            </a>
            <a className="button button--secondary button--large" href={GITHUB_URL} target="_blank" rel="noopener noreferrer">
              <Code2 size={19} /> View on GitHub
            </a>
          </div>
          <a className="final-cta__down" href="#home" aria-label="Back to top"><ArrowUp size={18} /></a>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
